import { useEffect, useRef, useState } from "react";
import { Api, ApiError, type BillingInfo, type BillingInterval, type BillingPlan } from "../api.js";
import { dictionaries, useI18n, type Lang } from "../i18n.js";
import { Badge } from "./ui.js";
import type { BillingIntent } from "../billingIntent.js";

const money = (amountMinor: number, currency: string, lang: Lang) =>
  new Intl.NumberFormat(lang === "es" ? "es" : "en-US", { style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(amountMinor / 100);
const day = (iso: string, lang: Lang) => new Intl.DateTimeFormat(lang === "es" ? "es" : "en-US", { dateStyle: "long", timeZone: "UTC" }).format(new Date(iso));

/** What the top-of-page banner should say, if anything, for an owner. Shared by the page and the app shell. */
export function billingBannerKey(b: BillingInfo, now = Date.now()): { key: "billing.banner.grace" | "billing.banner.suspended" | "billing.banner.ended" | "billing.banner.trial"; date?: string } | null {
  const c = b.current;
  if (c.state === "grace" && c.graceEndsAt) return { key: "billing.banner.grace", date: c.graceEndsAt };
  if (c.state === "suspended") return { key: "billing.banner.suspended" };
  if (c.state === "ended") return { key: "billing.banner.ended" };
  if (c.state === "trialing" && c.trialEnd && !c.cancelsAt) {
    const left = new Date(c.trialEnd).getTime() - now;
    if (left > 0 && left <= 3 * 86_400_000) return { key: "billing.banner.trial", date: c.trialEnd };
  }
  return null;
}

export function BillingBanner({ info }: { info: BillingInfo }) {
  const { t, lang } = useI18n();
  const b = billingBannerKey(info);
  if (!b) return null;
  return (
    <div className={`banner ${b.key === "billing.banner.trial" ? "ok" : "warn"} billing-banner`} role="region" aria-label={t("billing.title")}>
      <p>{t(b.key, { date: b.date ? day(b.date, lang) : "" })}</p>
      <a className="btn primary" href="#/billing">
        {t("billing.banner.cta")}
      </a>
    </div>
  );
}

/** The owner's billing facts for the app shell. A 404 (a deployment without billing) or any error just means "nothing to show". */
export function useBillingSummary(api: Api, enabled: boolean, refreshOn: unknown): BillingInfo | null {
  const [info, setInfo] = useState<BillingInfo | null>(null);
  useEffect(() => {
    let live = true;
    if (!enabled) {
      setInfo(null);
      return;
    }
    // Anything that is not a billing answer (an older server, a proxy page) just means there is nothing to show.
    api.billing().then((b) => live && setInfo(b && typeof b === "object" && b.current ? b : null), () => live && setInfo(null));
    return () => {
      live = false;
    };
  }, [api, enabled, refreshOn]);
  return info;
}

/** `warn`: say so when the limit is near or reached (conversations). Agents and teammates simply fill up, which is normal. */
function Meter({ label, used, limit, warn = false }: { label: string; used: number; limit: number | null; warn?: boolean }) {
  const { t, lang } = useI18n();
  const num = (v: number) => new Intl.NumberFormat(lang === "es" ? "es" : "en-US").format(v);
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const state = warn && limit && limit > 0 ? (used >= limit ? "full" : used >= limit * 0.8 ? "near" : "ok") : "ok";
  return (
    <div className="meter-row">
      <div className="row between">
        <span>{label}</span>
        <span>{limit === null ? t("billing.usedNoLimit", { used: num(used) }) : t("billing.usedOf", { used: num(used), limit: num(limit) })}</span>
      </div>
      {limit !== null && (
        <div className={`meter ${state}`} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(used, limit)}>
          <span style={{ width: `${pct}%` }} />
        </div>
      )}
      {state === "near" && <p className="hint">{t("billing.nearly")}</p>}
      {state === "full" && <p className="hint warn-text">{t("billing.full")}</p>}
    </div>
  );
}

function priceFor(plan: BillingPlan, interval: BillingInterval) {
  return plan.prices[interval] ?? null;
}

function PlanCard({ plan, interval, info, chosen, busy, onChoose }: { plan: BillingPlan; interval: BillingInterval; info: BillingInfo; chosen: boolean; busy: string | null; onChoose: (key: string) => void }) {
  const { t, lang } = useI18n();
  const price = priceFor(plan, interval);
  const isCurrent = info.current.plan?.key === plan.key;
  const trial = info.trialEligible && plan.trialDays > 0;
  // Only features that have wording are listed, so a new flag without a label is simply not shown.
  const features = plan.limits.features.filter((f) => `billing.feature.${f}` in dictionaries.en);
  return (
    <section className={`card plan${chosen ? " chosen" : ""}`} aria-labelledby={`plan-${plan.key}`}>
      <div className="row between">
        <h3 id={`plan-${plan.key}`}>{plan.name}</h3>
        {isCurrent && <Badge tone="ok">{t("billing.yourPlan")}</Badge>}
        {!isCurrent && chosen && <Badge tone="ok">{t("billing.chosen")}</Badge>}
      </div>
      {price ? (
        <>
          <p className="price">{t("billing.perMonth", { price: money(interval === "year" ? Math.round(price.amount / 12) : price.amount, price.currency, lang) })}</p>
          <p className="hint">{interval === "year" ? t("billing.billedYearly", { total: money(price.amount, price.currency, lang) }) : t("billing.billedMonthly")}</p>
        </>
      ) : null}
      <ul>
        <li>{plan.limits.agents === 1 ? t("billing.agent1") : t("billing.agentN", { n: plan.limits.agents ?? 0 })}</li>
        {features.map((f) => (
          <li key={f}>{t(`billing.feature.${f}` as never)}</li>
        ))}
      </ul>
      {info.canSubscribe && price && (
        <>
          <button className="btn primary" disabled={busy !== null} onClick={() => onChoose(plan.key)}>
            {busy === plan.key ? t("billing.opening") : trial ? t("billing.startTrial", { days: plan.trialDays }) : t("billing.subscribe")}
          </button>
          <p className="hint">{trial ? t("billing.trialNote") : t("billing.noTrialNote")}</p>
        </>
      )}
    </section>
  );
}

export function Billing({ api, intent, go = (url) => location.assign(url) }: { api: Api; intent?: BillingIntent | null; go?: (url: string) => void }) {
  const { t, lang } = useI18n();
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [interval, setIntervalState] = useState<BillingInterval>(intent?.interval ?? "year");
  const [waiting, setWaiting] = useState(() => /[?&]checkout=success\b/.test(location.hash));
  const [confirmed, setConfirmed] = useState(false);
  const cancelled = /[?&]checkout=cancelled\b/.test(location.hash);
  const tries = useRef(0);
  const waitingRef = useRef(waiting);
  waitingRef.current = waiting;

  const load = () =>
    api.billing().then(
      (b) => {
        setInfo(b);
        // The moment the plan shows up (from the webhook), stop waiting and say so.
        if (waitingRef.current && b.current.state !== "none") {
          setWaiting(false);
          setConfirmed(true);
        }
        return b;
      },
      (e) => {
        if (e instanceof ApiError && (e.status === 404 || e.status === 403)) setMissing(true);
        else setError(e instanceof ApiError ? e.message : t("common.network"));
        return null;
      },
    );

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Back from Stripe's checkout: the page proves nothing, so wait for the webhook to change the plan, then say so.
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(async () => {
      tries.current += 1;
      const b = await load();
      if ((b && b.current.state !== "none") || tries.current >= 20) {
        setWaiting(false);
        clearInterval(timer);
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  const choose = async (plan: string) => {
    setBusy(plan);
    setError(null);
    try {
      const r = await api.checkout(plan, interval);
      go(r.url);
      setBusy(null); // if the person comes back with the browser's back button, the buttons work again
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("common.network"));
      setBusy(null);
    }
  };
  const portal = async (flow?: "update") => {
    setBusy(flow ?? "portal");
    setError(null);
    try {
      go((await api.billingPortal(flow)).url);
      setBusy(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("common.network"));
      setBusy(null);
    }
  };

  if (missing) return <main className="page" id="main"><h1>{t("billing.title")}</h1><p>{t("billing.unavailable")}</p></main>;
  const c = info?.current;
  const plan = c?.plan ? info?.plans.find((p) => p.key === c.plan?.key) : undefined;
  const price = plan && c?.interval ? plan.prices[c.interval] : undefined;
  const hasPlan = !!c && (c.state === "active" || c.state === "trialing" || c.state === "grace" || c.state === "suspended");
  const intervals = (["month", "year"] as BillingInterval[]).filter((i) => info?.plans.some((p) => p.prices[i]));

  return (
    <main className="page" id="main">
      <h1>{t("billing.title")}</h1>
      <p>{t("billing.lead")}</p>
      {error && <div className="banner warn" role="alert">{error}</div>}
      {waiting && <div className="banner ok" role="status">{t("billing.processing")}</div>}
      {confirmed && <div className="banner ok" role="status">{t("billing.confirmed")}</div>}
      {!waiting && cancelled && <div className="banner" role="status">{t("billing.checkoutCancelled")}</div>}
      {!info && !error && <p>{t("common.loading")}</p>}
      {info && c && (
        <>
          <BillingBanner info={info} />
          <section className="card" aria-labelledby="billing-current">
            <div className="row between">
              <h2 id="billing-current">{t("billing.current")}</h2>
              <Badge tone={c.state === "active" || c.state === "trialing" ? "ok" : c.state === "none" ? "muted" : "warn"}>{t(`billing.state.${c.state}` as never)}</Badge>
            </div>
            {c.plan ? <p className="big">{c.plan.name}{price && c.interval ? ` · ${money(price.amount, price.currency, lang)}${c.interval === "year" ? " / " + (lang === "es" ? "año" : "year") : " / " + (lang === "es" ? "mes" : "month")}` : ""}</p> : <p>{t("billing.noPlan")}</p>}
            {c.state === "trialing" && c.trialEnd && !c.cancelsAt && price && <p>{t("billing.trialEnds", { date: day(c.trialEnd, lang), amount: money(price.amount, price.currency, lang) })}</p>}
            {c.state === "active" && c.periodEnd && !c.cancelsAt && <p>{t("billing.renews", { date: day(c.periodEnd, lang) })}</p>}
            {c.cancelsAt && <p>{t("billing.cancels", { date: day(c.cancelsAt, lang) })}</p>}
            {c.grantedByStaff && <p className="hint">{t("billing.grantedByStaff")}</p>}
            {info.heldAgents.length > 0 && <div className="banner warn" role="status">{t("billing.held", { n: info.heldAgents.length })}</div>}
            {hasPlan && !c.grantedByStaff && (
              <div className="row">
                <button className="btn primary" disabled={busy !== null} onClick={() => void portal()}>
                  {busy === "portal" ? t("billing.opening") : t("billing.manage")}
                </button>
                <button className="btn" disabled={busy !== null} onClick={() => void portal("update")}>
                  {busy === "update" ? t("billing.opening") : t("billing.change")}
                </button>
              </div>
            )}
          </section>

          {c.state !== "none" && c.state !== "ended" && (
          <section className="card" aria-labelledby="billing-usage">
            <h2 id="billing-usage">{t("billing.usage")}</h2>
            <Meter warn label={t("billing.usageConversations")} used={info.usage.conversations.used} limit={info.usage.conversations.limit} />
            <Meter label={t("billing.usageAgents")} used={info.usage.agents.used} limit={info.usage.agents.limit} />
            <Meter label={t("billing.usageTeam")} used={info.usage.teammates.used} limit={info.usage.teammates.limit} />
          </section>
          )}

          {info.canSubscribe && (
            <section aria-labelledby="billing-plans">
              <div className="row between">
                <h2 id="billing-plans">{t("billing.plans")}</h2>
                {intervals.length > 1 && (
                  <div className="row" role="group" aria-label={t("billing.plans")}>
                    {intervals.map((i) => (
                      <button key={i} className={`btn${interval === i ? " primary" : ""}`} aria-pressed={interval === i} onClick={() => setIntervalState(i)}>
                        {t(i === "month" ? "billing.monthly" : "billing.yearly")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="plans">
                {info.plans
                  .filter((p) => p.prices[interval])
                  .map((p) => (
                    <PlanCard key={p.key} plan={p} interval={interval} info={info} chosen={intent?.plan === p.key} busy={busy} onChoose={(k) => void choose(k)} />
                  ))}
              </div>
            </section>
          )}

          <section className="card" aria-labelledby="billing-invoices">
            <h2 id="billing-invoices">{t("billing.invoices")}</h2>
            {info.invoices.length === 0 ? (
              <p>{t("billing.noInvoices")}</p>
            ) : (
              <table>
                <tbody>
                  {info.invoices.map((i) => (
                    <tr key={i.id}>
                      <td>{day(i.date, lang)}</td>
                      <td>{money(i.status === "paid" ? i.amountPaid : i.amountDue, i.currency, lang)}</td>
                      <td>{t(`billing.inv.${i.status}` as never) === `billing.inv.${i.status}` ? i.status : t(`billing.inv.${i.status}` as never)}</td>
                      <td>{i.url && <a href={i.url} target="_blank" rel="noreferrer">{t("billing.invoiceView")}<span className="sr-only"> {t("billing.title")}</span></a>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
